/**
 * The wire form of a reference the user pinned with `@` in the composer, and how it is
 * turned back into something readable.
 *
 * A pinned reference travels to the agent inside the prompt as
 * `@[Individuals using the Internet](indicator:HHU4212ab-%_INT--)`,
 * `@[Oman](country:OMN)` or `@[Internet usage, Italy](chart:a3f91c8e2b7d4506)`. That text is
 * what gets stored in the session history, so both the live bubble and a reloaded
 * conversation have to render it as a chip rather than showing the brackets to the user.
 *
 * The three kinds differ in where they come from, not in how they travel: indicators and
 * countries are picked from the `@` menu, while a chart is pinned by clicking the chart
 * itself — there is nothing to search for, and the id is not something a user could type.
 */

/** Matches one pinned reference. Kept in one place so every renderer agrees on the shape. */
export const MENTION_PATTERN = /@\[([^\]\n]+)\]\((indicator|country|region|chart):([^)\n]+)\)/g;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;',
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

/**
 * Builds the chip markup around text the caller has already prepared.
 *
 * `labelHtml` is rendered verbatim, `@` included — the caller owns it because the preview
 * has to count that character against its length budget, and it once ended up doubled by
 * being added at both ends.
 *
 * Both arguments must already be HTML-escaped: callers differ in when they escape (the
 * markdown pipe escapes just these two fields, the plain-text pipe escapes the whole
 * string up front), and escaping twice would show `&amp;lt;` to the user.
 */
export function mentionChip(labelHtml: string, kind: string, codeHtml: string): string {
  const variant =
    kind === 'country' ? ' is-country' : kind === 'region' ? ' is-region' : kind === 'chart' ? ' is-chart' : '';
  // The sidebar preview passes no code: a nested tooltip there would shadow the row's own
  // title with a half-empty one.
  const title = codeHtml ? ` title="${kind}: ${codeHtml}"` : '';
  return `<span class="mention-chip${variant}"${title}>${labelHtml}</span>`;
}

/**
 * A chart title reduced to something that can serve as a mention label.
 *
 * The label is matched back out of the composer text by `findMention`, and it is written
 * into `@[label](chart:id)` on the way out, so anything that would break either — brackets,
 * parentheses, newlines — has to go. An empty or title-less figure falls back to a word
 * rather than to an empty chip.
 */
export function chartMentionLabel(title: string | undefined | null): string {
  const cleaned = (title ?? '')
    .replace(/[[\]()\n\r]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return 'chart';
  return cleaned.length > 32 ? `${cleaned.slice(0, 31).trimEnd()}…` : cleaned;
}

/** The same text with each pinned reference reduced to `@Label`. */
export function mentionPlainText(value: string): string {
  return value.replace(MENTION_PATTERN, (_match, label: string) => `@${label}`);
}

/**
 * Escaped HTML for a one-line preview: references become chips, everything else stays
 * literal, and the result is cut to `maxLength` *visible* characters.
 *
 * Truncating the raw string instead would be wrong twice over — the wire form is far
 * longer than what the reader sees (`@[Households with a TV](indicator:HHA965-%_HH--)`
 * is 47 characters for a 20-character label), and a cut landing mid-pattern would leak
 * brackets into the UI.
 */
export function renderMentionPreview(value: string, maxLength: number): string {
  let html = '';
  let used = 0;
  let cursor = 0;

  const take = (text: string): string => {
    const room = maxLength - used;
    if (room <= 0) return '';
    const slice = text.slice(0, room);
    used += slice.length;
    return slice;
  };

  MENTION_PATTERN.lastIndex = 0;
  for (let match = MENTION_PATTERN.exec(value); match; match = MENTION_PATTERN.exec(value)) {
    const [whole, label, kind] = match;
    html += escapeHtml(take(value.slice(cursor, match.index)));
    if (used < maxLength) {
      html += mentionChip(escapeHtml(take(`@${label}`)), kind, '');
    }
    cursor = match.index + whole.length;
  }
  html += escapeHtml(take(value.slice(cursor)));

  const visible = mentionPlainText(value).length;
  return visible > maxLength ? `${html}…` : html;
}
